// src/pages/RegisterEvent.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../App.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUserEdit, faGear } from "@fortawesome/free-solid-svg-icons";
import {
  SwipeableList,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
  Type as ListType,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';

import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import PrelegentEditorModal from "../modals/PrelegentEditorModal";
import AgendaBar from "../components/AgendaBar";

/* ------------------------- CONST & LOG ------------------------- */
const API = "https://api18081113.sayq.pl";
const rlog = (...a) => console.log("%c[RE]", "color:#0ea5e9;font-weight:700;", ...a);
const rwarn = (...a) => console.warn("%c[RE]", "color:#f59e0b;font-weight:700;", ...a);
const rerr = (...a) => console.error("%c[RE]", "color:#ef4444;font-weight:700;", ...a);

/* ------------------------- HELPERS ------------------------- */
const getFromLS = (k, d = "") => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const setToLS = (k, v) => { try { localStorage.setItem(k, v); } catch { } };
const getMapLS = (sec) => { try { return JSON.parse(localStorage.getItem(`segmentsMap:${sec}`) || "{}"); } catch { return {}; } };
const setMapLS = (sec, m) => { try { localStorage.setItem(`segmentsMap:${sec}`, JSON.stringify(m)); } catch { } };

const toUnderscore = (s) => String(s || "").trim().replace(/\s+/g, "_");
const toHuman = (s) => String(s || "").replace(/_/g, " ");
const toKey = (s) => toUnderscore(s).toLowerCase();
const hasSecretPivot = (name, secId) => typeof name === "string" && name.includes(`_${secId}_`);

const collectStringsShallow = (x) => {
  const out = [];
  const pushIf = (v) => {
    if (typeof v === "string") out.push(v);
    else if (v != null && (typeof v === "number" || typeof v === "boolean")) out.push(String(v));
  };
  if (Array.isArray(x)) {
    x.forEach((v) => {
      if (typeof v === "string") out.push(v);
      else if (v && typeof v === "object") Object.values(v).forEach(pushIf);
    });
  } else if (x && typeof x === "object") {
    Object.values(x).forEach((v) => {
      if (typeof v === "string") out.push(v);
      else if (Array.isArray(v)) v.forEach(pushIf);
      else if (v && typeof v === "object") Object.values(v).forEach(pushIf);
    });
  }
  return out;
};

const displaySegmentName = (fullName, secId) => {
  if (typeof fullName !== "string") return "";
  const pivot = `_${secId}_`;
  const base = fullName.includes(pivot) ? fullName.split(pivot)[0] : fullName;
  return toHuman(base);
};

const toISODate = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  let m = s.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/);
  if (m) {
    const [, y, mo, d] = m;
    return `${y.padStart(4, "0")}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${String(y).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  const dt = new Date(s);
  if (!isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const mo = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }
  return "";
};

const normalizeEventMedia = (val, subdir = "") => {
  if (val == null) return "";
  let s = String(val).trim();
  if (!s) return "";
  if (/^(null|undefined)$/i.test(s)) return "";
  if (/^https?:\/\//i.test(s)) return s;
  s = s.replace(/^\/+/, "");
  if (!s) return "";
  const base = "https://eventicon.sayq.pl";
  if (s.includes("/")) return `${base}/${s}`;
  return subdir ? `${base}/${subdir}/${s}` : `${base}/${s}`;
};

/* ------------------------- NORMALIZATORY PRELEGENTÓW ------------------------- */
const normalizePrelegentsPayload = (payload) => {
  let rows = null;
  if (Array.isArray(payload)) rows = payload;
  if (!rows && Array.isArray(payload?.data)) rows = payload.data;
  if (!rows && Array.isArray(payload?.results)) rows = payload.results;
  if (!rows && Array.isArray(payload?.rows)) rows = payload.rows;
  if (!rows && payload && typeof payload === "object") {
    const vals = Object.values(payload).filter(v => v && typeof v === "object");
    if (vals.length) rows = vals;
  }
  if (!rows) return [];
  return rows.map((r) => {
    if (!r) return null;
    if (typeof r === "string") {
      return {
        segment_name: r,
        stage_name: "",
        prelegent_name: "",
        prelegent_info: "",
        prelegent_link: "",
        prelegent_img: "",
        prelegent_bg: "",
        prelegent_location: "",
      };
    }
    if (typeof r === "object") {
      // mapujemy stage_name (różne warianty), i zwracamy go w obiekcie
      const stageName = (r.stage_name ?? r.stageName ?? r.stage ?? r["stage-name"] ?? r.stage_name ?? "").trim();
      return {
        segment_name: (r.segment_name || r.name || r.segment || "").trim(),
        stage_name: stageName,
        prelegent_name: (r.prelegent_name ?? r.speaker_name ?? "").trim(),
        prelegent_info: (r.prelegent_info ?? r.speaker_info ?? "").trim(),
        prelegent_link: (r.prelegent_link ?? r.speaker_link ?? "").trim(),
        prelegent_img: (r.prelegent_img ?? r["prelegent-img"] ?? r.speaker_img ?? r.photo ?? "").trim(),
        prelegent_bg: (r.prelegent_bg ?? r["prelegent-bg"] ?? r.speaker_bg ?? r.background ?? "").trim(),
        prelegent_location: (r.prelegent_location ?? r.speaker_location ?? "").trim(),
      };
    }
    return null;
  }).filter(Boolean);
};

const normalizeStageRows = (payload) => {
  let rows = [];
  if (Array.isArray(payload?.data)) rows = payload.data;
  else if (Array.isArray(payload)) rows = payload;
  else if (Array.isArray(payload?.rows)) rows = payload.rows;
  else if (Array.isArray(payload?.results)) rows = payload.results;
  else if (payload && typeof payload === "object")
    rows = Object.values(payload).filter((x) => x && typeof x === "object");

  return normalizePrelegentsPayload(rows);
};

/* ------------------------- COMPONENT ------------------------- */
export default function RegisterEvent() {
  const navigate = useNavigate();
  const { id } = useParams();

  const getSecretId = () => id || getFromLS("secretId") || "";

  const generateStageId = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  // generator 6-znakowego kodu (a-z0-9) — użyjemy go jako nazwy tabeli
  const generate6CharCode = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    let out = "";
    for (let i = 0; i < 6; i++) {
      out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
  };

  // [GROUP] 9-cyfrowy kod – bez self/globalThis i bez błędów lintera
  const generate9DigitCode = () => {
    const hasCrypto =
      typeof window !== "undefined" &&
      window.crypto &&
      typeof window.crypto.getRandomValues === "function";

    if (hasCrypto) {
      const buf = new Uint32Array(1);
      window.crypto.getRandomValues(buf);
      const n = (buf[0] % 900000000) + 100000000; // 100,000,000..999,999,999
      return String(n);
    }

    // fallback
    return String(Math.floor(Math.random() * 900000000) + 100000000);
  };

  /* --------------------------- STATE --------------------------- */
  const [secretId, setSecretId] = useState(getSecretId());
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [eventData, setEventData] = useState({
    date: "",
    adress: "",
    organizator: "",
    eventName: "",
    eventImgUrl: "",
    eventImgFile: null,
  });

  const [segments, setSegments] = useState([]);  // Array<{ full: string|null, short: string }>
  const [segmentInput, setSegmentInput] = useState("");

  const [prelegents, setPrelegents] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRow, setEditorRow] = useState(null);

  // Agenda: od razu w trybie edycji, ale BEZ auto-zapisu — zapis tylko w „Zapisz i zakończ”
  const [agenda, setAgenda] = useState("");
  const [agendaInput, setAgendaInput] = useState("");
  const [showAgendaInput, setShowAgendaInput] = useState(true);

  const [listSegMap, setListSegMap] = useState({}); // shortKey -> full

  // [GROUP] per-segment busy/info
  const [groupBusy, setGroupBusy] = useState({});   // { [fullName]: true }
  const [groupInfo, setGroupInfo] = useState({});   // { [fullName]: { code, ok } }

  // [GROUP PICKER] nowy stan — lista grup do przypisania
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [groupPickerFor, setGroupPickerFor] = useState(null); // { full, short }
  const [groupsMasterList, setGroupsMasterList] = useState([]); // [{segment_name, group_id}]
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Tutorial / Animation state
  const [tutorialTarget, setTutorialTarget] = useState(null); // Index of row to animate
  const prevSegmentsLen = useRef(0);

  // Trigger tutorial on Step 2 entry (if segments exist)
  useEffect(() => {
    if (step === 2 && segments.length > 0) {
      // Short delay to ensure render
      const t = setTimeout(() => {
        setTutorialTarget(0);
        setTimeout(() => setTutorialTarget(null), 1500);
      }, 500);
      return () => clearTimeout(t);
    }
  }, [step, segments.length > 0]);

  // Trigger tutorial when a new segment is added
  useEffect(() => {
    if (segments.length > prevSegmentsLen.current && prevSegmentsLen.current > 0) {
      // A segment was added
      const newIndex = segments.length - 1;
      setTutorialTarget(newIndex);
      setTimeout(() => setTutorialTarget(null), 1500);
    }
    prevSegmentsLen.current = segments.length;
  }, [segments.length]);

  /* --------------------------- FETCHERS --------------------------- */
  const fetchSegments = async (secId) => {
    try {
      const url = `${API}/segments/${encodeURIComponent(secId)}`;
      rlog("→ segments:", url);
      const t0 = performance.now();
      const res = await fetch(url, { mode: "cors" });
      const json = await res.json();
      rlog("← segments:", { status: res.status, ok: res.ok, time_s: ((performance.now() - t0) / 1000).toFixed(3) });

      if (!res.ok) return [];
      const raw = Array.isArray(json) ? json
        : (Array.isArray(json?.segments) && json.segments)
        || (Array.isArray(json?.data) && json.data)
        || (Array.isArray(json?.rows) && json.rows)
        || (Array.isArray(json?.results) && json.results)
        || (json && typeof json === "object" ? Object.values(json).filter(v => v && typeof v === "object") : [])
        || [];

      const items = raw.map((row) => {
        // jeśli row jest obiektem i ma stage_name, użyjemy go jako short (czytelna nazwa)
        const stageName = row && typeof row === "object"
          ? (row.stage_name || row.stageName || row.stage || row["stage-name"] || "")
          : "";
        const val = typeof row === "string"
          ? row
          : (row?.full_name || row?.fullName || row?.segment_full || row?.name || row?.segment_name || row?.segment || "");
        if (!val && !stageName) return null;

        if (stageName && String(stageName).trim()) {
          // mamy bezpośrednią czytelną nazwę dla tego wpisu
          return { full: val || null, short: toHuman(String(stageName).trim()) };
        }

        if (val && hasSecretPivot(val, secId)) {
          return { full: val, short: displaySegmentName(val, secId) };
        }
        return { full: null, short: toHuman(val || stageName) };
      }).filter(Boolean);

      const uniq = [];
      const seen = new Set();
      for (const it of items) {
        const key = (it.full || it.short).toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        uniq.push(it);
      }
      return uniq;
    } catch (err) {
      rerr("Błąd pobierania segmentów:", err);
      return [];
    }
  };

  const fetchPrelegentsLegacy = async (secId) => {
    try {
      const url = `${API}/get-prelegent-info/${encodeURIComponent(secId)}`;
      rlog("→ prelegents (legacy):", url);
      const t0 = performance.now();
      const res = await fetch(url, { mode: "cors" });
      const time = (performance.now() - t0) / 1000;

      let text = "";
      let json = {};
      try { text = await res.text(); json = JSON.parse(text); } catch { }
      rlog("← prelegents (legacy):", { status: res.status, ok: res.ok, time_s: time.toFixed(3) });

      const serverMsg = String(json?.message || json?.error || text || "");

      if (!res.ok) {
        const legacyNoBg = res.status === 500 && /Unknown column\s+'prelegent_bg'/i.test(serverMsg);
        if (legacyNoBg) return [];
        rwarn("Błąd HTTP przy pobieraniu prelegentów (legacy):", res.status, serverMsg.slice(0, 300));
        return [];
      }

      return normalizePrelegentsPayload(json);
    } catch (err) {
      rerr("Błąd pobierania prelegentów (legacy):", err);
      return [];
    }
  };

  const fetchListSegments = async (secId) => {
    try {
      const url = `${API}/list-segments`;
      rlog("→ list-segments:", url);
      const t0 = performance.now();
      const res = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId: secId }),
      });
      const json = await res.json().catch(() => ({}));
      rlog("← list-segments:", { status: res.status, ok: res.ok, time_s: ((performance.now() - t0) / 1000).toFixed(3) });

      if (!res.ok || json?.success === false || !Array.isArray(json?.data)) {
        rwarn("list-segments: unexpected response", { status: res.status, json });
        return { list: [], map: {} };
      }

      const list = json.data
        .map((r) => {
          const full = typeof r?.full === "string" ? r.full : (typeof r?.segment_name === "string" ? r.segment_name : "");
          if (!full) return null;
          // jeśli API zwraca stage_name dla entry, preferuj ją (niektóre serwery dołączają)
          const stageName = (r?.stage_name || r?.stageName || r?.stage || "") || "";
          const short = stageName ? toHuman(stageName) : displaySegmentName(full, secId);
          return { full, short };
        })
        .filter(Boolean);

      const map = {};
      for (const it of list) {
        const key = toKey(it.short);
        if (it.full && key) map[key] = it.full;
      }

      return { list, map };
    } catch (err) {
      rerr("Błąd list-segments:", err);
      return { list: [], map: {} };
    }
  };

  const fetchAllStageData = async (secId) => {
    try {
      const url = `${API}/event/get-all-stage-data`;
      rlog("→ get-all-stage-data:", url);
      const t0 = performance.now();
      const res = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId: secId }),
      });
      const json = await res.json().catch(() => ({}));
      rlog("← get-all-stage-data:", { status: res.status, ok: res.ok, time_s: ((performance.now() - t0) / 1000).toFixed(3) });

      if (!res.ok) return [];
      return normalizeStageRows(json);
    } catch (err) {
      rerr("Błąd get-all-stage-data:", err);
      return [];
    }
  };

  const mergeSegments = (segList, preList, secId, mapLS) => {
    const out = segList.map((it) => ({ ...it }));

    for (const it of out) {
      if (it.full) continue;
      const shortKey = toKey(it.short);
      const found = preList.find((p) => {
        const seg = (p.segment_name || "").toLowerCase().trim();
        return seg.startsWith(`${shortKey}_`) && hasSecretPivot(p.segment_name, secId);
      });
      if (found) {
        it.full = found.segment_name;
        // preferuj stage_name z prelegenta jako short
        if (found.stage_name && String(found.stage_name).trim()) it.short = String(found.stage_name).trim();
        else it.short = displaySegmentName(found.segment_name, secId);
      }
    }

    for (const it of out) {
      if (!it.full) {
        const fromMap = mapLS[toKey(it.short)];
        if (fromMap && hasSecretPivot(fromMap, secId)) it.full = fromMap;
      }
    }

    rlog("mergeSegmentsWithPrelegents+LS →", out);
    return out;
  };

  const refreshStep2Data = async (secId) => {
    await fetch(`${API}/create-event-segments-table`, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secretId: secId }),
    }).catch(() => { });

    const [segs, legPre, listResp, allStage] = await Promise.all([
      fetchSegments(secId),
      fetchPrelegentsLegacy(secId),
      fetchListSegments(secId),
      fetchAllStageData(secId),
    ]);

    const mapLS0 = getMapLS(secId);
    const mapLS1 = { ...mapLS0, ...listResp.map };
    setMapLS(secId, mapLS1);
    setListSegMap(listResp.map || {});

    const byFull = (arr) => Object.fromEntries(arr.map((r) => [r.segment_name, r]));
    const allMap = byFull(allStage);
    const legMap = byFull(legPre);
    const mergedPre = Object.values({ ...legMap, ...allMap });

    setPrelegents(mergedPre);

    const mergedSegments = mergeSegments(segs, mergedPre, secId, mapLS1);
    setSegments(mergedSegments);

    try {
      const res = await fetch(`${API}/event-settings/${secId}`, { mode: "cors" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data) {
        const imgField = data["event-img"] ?? data["event_img"] ?? data["img"] ?? "";
        const dateRaw = data?.date || data?.event_date || "";
        setEventData((prev) => ({
          ...prev,
          eventImgUrl: normalizeEventMedia(imgField, "event_logo") || prev.eventImgUrl,
          date: toISODate(dateRaw) || prev.date,
          eventImgFile: null,
        }));
      }
    } catch { }
  };

  /* --------------------------- EFFECTS --------------------------- */
  useEffect(() => {
    const currentId = getSecretId();
    if (currentId) {
      setSecretId(currentId);
      setToLS("secretId", currentId);
      rlog("secretId set:", currentId);
    }
  }, [id]);

  useEffect(() => {
    if (!secretId) return;
    (async () => {
      try {
        const r1 = await fetch(`${API}/event-settings/${secretId}`, { mode: "cors" });
        const s1 = await r1.json().catch(() => ({}));

        const r2 = await fetch(`${API}/event-by-secret/${secretId}`, { mode: "cors" });
        const s2 = await r2.json().catch(() => ({}));

        const event_name =
          (r1.ok && (s1?.event_name ?? s1?.["event-name"])) ||
          (r2.ok && (s2?.event_name ?? s2?.["event-name"])) || "";

        const dateRaw =
          (r1.ok && (s1?.date || s1?.event_date)) ||
          (r2.ok && (s2?.date || s2?.event_date)) || "";

        const adress =
          (r1.ok && s1?.adress) || (r2.ok && s2?.adress) || "";

        const organizator =
          (r1.ok && s1?.organizator) || (r2.ok && s2?.organizator) || "";

        const imgRaw =
          (r1.ok && (s1?.["event-img"] ?? s1?.["event_img"] ?? s1?.img)) ||
          (r2.ok && (s2?.["event-img"] ?? s2?.["event_img"] ?? s2?.img)) || "";

        setEventData((prev) => ({
          ...prev,
          eventName: event_name || prev.eventName,
          date: toISODate(dateRaw) || prev.date,
          adress: adress || prev.adress,
          organizator: organizator || prev.organizator,
          eventImgUrl: normalizeEventMedia(imgRaw, "event_logo") || prev.eventImgUrl,
          eventImgFile: null,
        }));
        rlog("Event loaded:", { event_name, dateRaw, adress, organizator, imgRaw });
      } catch (err) {
        rerr("Błąd pobierania danych wydarzenia:", err);
      }
    })();
  }, [secretId]);

  useEffect(() => {
    if (step !== 2) return;
    const storedId = getSecretId();
    if (!storedId) return;
    (async () => {
      try {
        await refreshStep2Data(storedId);
      } catch (e) {
        rerr("Błąd w kroku 2:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Krok 3 – od razu edycja agendy, ale zapis tylko w „Zapisz i zakończ”
  useEffect(() => {
    if (step === 3) setShowAgendaInput(true);
  }, [step]);

  /* --------------------------- VALIDATION --------------------------- */
  const isStep1Valid = useMemo(() => {
    return (
      eventData.eventName.trim().length > 0 &&
      eventData.date.trim().length > 0 &&
      eventData.adress.trim().length > 0 &&
      eventData.organizator.trim().length > 0
    );
  }, [eventData]);

  /* --------------------------- RESOLVER DO EDYCJI --------------------------- */
  const resolveFullForEdit = async (shortHuman) => {
    const shortKey = toKey(shortHuman);

    const local = segments.find((s) => s.full && s.full.toLowerCase().startsWith(`${shortKey}_`) && hasSecretPivot(s.full, secretId));
    if (local?.full) return local.full;

    const fromMapState = listSegMap[shortKey];
    if (fromMapState && hasSecretPivot(fromMapState, secretId)) return fromMapState;

    const mapLS = getMapLS(secretId);
    const fromMapLS = mapLS[shortKey];
    if (fromMapLS && hasSecretPivot(fromMapLS, secretId)) return fromMapLS;

    const foundPre = prelegents.find((r) => {
      const seg = (r.segment_name || "").toLowerCase().trim();
      return seg.startsWith(`${shortKey}_`) && hasSecretPivot(r.segment_name, secretId);
    });
    if (foundPre?.segment_name) return foundPre.segment_name;

    try {
      const resS = await fetch(`${API}/segments/${encodeURIComponent(secretId)}`, { mode: "cors" });
      const jsonS = await resS.json().catch(() => ({}));
      const strings = collectStringsShallow(jsonS);
      const remoteFull = strings.find((s) => {
        if (typeof s !== "string") return false;
        const low = s.toLowerCase();
        return low.startsWith(`${shortKey}_`) && hasSecretPivot(s, secretId);
      });
      if (remoteFull) return remoteFull;
    } catch { }

    try {
      const { map } = await fetchListSegments(secretId);
      if (map && map[shortKey] && hasSecretPivot(map[shortKey], secretId)) {
        const full = map[shortKey];
        const mergedMap = { ...mapLS, ...map };
        setMapLS(secretId, mergedMap);
        setListSegMap(map);
        return full;
      }
    } catch { }

    rwarn("[resolveFullForEdit] NOT FOUND full for short:", shortHuman);
    return null;
  };

  /* --------------------------- HANDLERS --------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "date") setEventData({ ...eventData, [name]: toISODate(value) });
    else setEventData({ ...eventData, [name]: value });
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEventData((prev) => ({ ...prev, eventImgFile: file, eventImgUrl: URL.createObjectURL(file) }));
  };

  const handleRemoveLogo = () => {
    setEventData((prev) => ({ ...prev, eventImgFile: null, eventImgUrl: "" }));
  };

  // DODAWANIE SEGMENTU — zmodyfikowane: używamy 6-znakowego code jako nazwy tabeli
  const addSegment = async () => {
    const sec = getSecretId();
    if (!sec) return;
    const input = (segmentInput || "").trim();
    if (!input) return;

    const stageId = generateStageId();
    const stageCode = generate6CharCode();            // <-- używamy tego jako nazwy tabeli
    const fullName = `${stageCode}_${sec}_${stageId}`; // <-- to id/segment_name w DB
    const shortHuman = input; // nazwa czytelna, zapisywana do event_segments_${sec}

    try {
      // 1) tworzymy tabelę dla segmentu — przekazujemy jako stageName 6-znakowy kod
      const response = await fetch(`${API}/create-stage-table`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId: sec, stageName: stageCode, stageId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.error) {
        rerr(result?.error || "Błąd tworzenia segmentu (create-stage-table)");
        return;
      }

      // 2) zarejestruj segment w tabeli prelegentów (DB oczekuje segment_name) — przekazujemy CODE_secretId_stageId
      //    oraz dodatkowo przesyłamy stage_name do zapisania w event_segments_${secretId}
      try {
        const payload = {
          secretId: sec,
          segment_name: fullName,
          stage_name: shortHuman,
          stage_code: stageCode,
          stageId,
        };
        const res = await fetch(`${API}/update-prelegent-info`, {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        // nie wymagamy ścisłej walidacji odpowiedzi - backend może zwracać różne struktury
        const txt = await res.text().catch(() => "");
        rlog("← update-prelegent-info:", res.status, txt.slice ? txt.slice(0, 300) : txt);
      } catch (e) {
        rwarn("Nie udało się wywołać update-prelegent-info:", e);
      }

      // 3) zaktualizuj mapę w localStorage żeby przyszłe odwołania mogły znaleźć mapping shortKey -> full
      const map = getMapLS(sec);
      const shortKey = toKey(shortHuman);
      map[shortKey] = fullName;
      setMapLS(sec, map);

      // 4) zaktualizuj UI: dodajemy segment z pełną nazwą (full) oraz short jako czytelną nazwą
      setSegments((prev) => [...prev, { full: fullName, short: shortHuman }]);
      setSegmentInput("");

      // 5) odśwież dane kroku 2 (prelegenci, listy itp.)
      await refreshStep2Data(sec);
    } catch (err) {
      rerr("Błąd połączenia podczas dodawania segmentu:", err);
    }
  };

  const requestDelete = (fullName) => {
    setDeleteTarget(fullName);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      rerr("Confirm Delete: No target set!");
      return;
    }
    const fullName = deleteTarget;
    console.log("CONFIRM DELETE:", fullName);
    const sec = getSecretId();

    try {
      const res = await fetch(`${API}/delete-prelegent-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId: sec, segment_name: fullName }),
      });

      const data = await res.json();

      if (data.success) {
        setSegments(prev => prev.filter(s => s.full !== fullName));
        await refreshStep2Data(sec);
        setMessage("");
      } else {
        rerr("Delete failed:", data.message);
        setMessage("Nie udało się usunąć segmentu: " + (data.message || "Błąd serwera"));
      }
    } catch (e) {
      rerr("Delete error:", e);
      setMessage("Błąd połączenia z serwerem.");
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const saveDateImmediate = async () => {
    const sec = getSecretId();
    if (!sec) return;
    const isoDate = toISODate(eventData.date);
    try {
      const fd = new FormData();
      fd.append("secred-id", sec);
      fd.append("date", isoDate);
      fd.append("event_date", isoDate);
      fd.append("event_name", eventData.eventName || "");
      fd.append("adress", eventData.adress || "");
      fd.append("organizator", eventData.organizator || "");

      const res = await fetch(`${API}/update-event-settings`, { method: "POST", mode: "cors", body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.error) rwarn("Nie udało się zapisać daty:", json?.error);
    } catch (e) {
      rerr("Błąd zapisu daty:", e);
    }
  };

  // Zapis agendy – TYLKO z „Zapisz i zakończ”
  const saveAgenda = async () => {
    const sec = getSecretId();
    if (!sec) return false;

    const payload = {
      secretId: sec,
      agenda: (agendaInput ?? "").trim(),
    };

    try {
      const res = await fetch(`${API}/save-agenda`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.success === false) {
        rwarn("Nie udało się zapisać agendy:", json);
        return false;
      }

      setAgenda(payload.agenda); // zsynchronizuj „zapisane”
      rlog("Agenda zapisana (final).");
      return true;
    } catch (e) {
      rerr("Błąd zapisu agendy:", e);
      return false;
    }
  };

  const handleSaveAll = async () => {
    const sec = getSecretId();
    if (!sec) return;
    setIsSaving(true);
    setMessage("");
    rlog("SAVE ALL start");

    try {
      // 1) Agenda
      await saveAgenda();

      // 2) Logo + dane eventu
      let uploadedImageFilename = "";
      if (eventData.eventImgFile) {
        const formDataUpload = new FormData();
        formDataUpload.append("secred-id", sec);
        formDataUpload.append("event-img", eventData.eventImgFile);

        const resUpload = await fetch(`${API}/upload-event-image-base64`, { method: "POST", mode: "cors", body: formDataUpload });
        const uploadText = await resUpload.text();
        let uploadJson = {};
        try { uploadJson = JSON.parse(uploadText); } catch { }
        if (!resUpload.ok || !uploadJson?.success || !uploadJson?.url) {
          throw new Error(uploadJson?.message || uploadText || "Błąd uploadu logo");
        }
        uploadedImageFilename = String(uploadJson.url).split("/").pop();
      }

      const isoDate = toISODate(eventData.date);
      const formDataSave = new FormData();
      formDataSave.append("secred-id", sec);
      formDataSave.append("event_name", eventData.eventName);
      formDataSave.append("date", isoDate);
      formDataSave.append("event_date", isoDate);
      formDataSave.append("adress", eventData.adress);
      formDataSave.append("organizator", eventData.organizator);
      if (uploadedImageFilename) formDataSave.append("event-img", uploadedImageFilename);

      const resSave = await fetch(`${API}/update-event-settings`, { method: "POST", mode: "cors", body: formDataSave });
      const saveText = await resSave.text();
      let saveJson = {};
      try { saveJson = JSON.parse(saveText); } catch { }
      if (!resSave.ok || saveJson?.error) throw new Error(saveJson?.error || saveText || "Błąd zapisu danych wydarzenia");

      await fetch(`${API}/create-guest-table`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId: sec }),
      });

      setMessage("Zaktualizowano dane wydarzenia");
      setTimeout(() => navigate("/thank-you"), 800);
    } catch (error) {
      rerr("Błąd procesu zapisu:", error);
      setMessage(error?.message || "Coś poszło nie tak");
    } finally {
      setIsSaving(false);
    }
  };

  const findPrelegentBySegment = (segment_name) =>
    prelegents.find((r) => (r.segment_name || "").trim() === (segment_name || "").trim());

  // [GROUP] Utwórz grupę dla segmentu
  const createGroupForSegment = async ({ shortName, fullName }) => {
    const sec = getSecretId();
    if (!sec) return;

    let full = fullName;
    if (!full || !hasSecretPivot(full, secretId)) {
      full = await resolveFullForEdit(shortName);
    }
    if (!full || !hasSecretPivot(full, secretId)) {
      rwarn("[group] Nie udało się ustalić pełnej nazwy segmentu dla:", shortName);
      return;
    }

    if (groupBusy[full]) return; // anty-duble
    setGroupBusy((m) => ({ ...m, [full]: true }));

    const code = generate9DigitCode();
    const payload = {
      segment_name: full,
      group_id: code,
      group_master: 1,
    };

    try {
      const t0 = performance.now();
      const res = await fetch(`${API}/update-group-settings`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const txt = await res.text();
      let json = {};
      try { json = JSON.parse(txt); } catch { }
      rlog("← update-group-settings:", { status: res.status, ok: res.ok, time_s: ((performance.now() - t0) / 1000).toFixed(3), json });

      const ok = res.ok && (json?.success !== false);
      setGroupInfo((g) => ({ ...g, [full]: { code, ok } }));
      if (!ok) {
        rwarn("[group] Serwer zwrócił błąd:", txt?.slice?.(0, 300) || json);
        setMessage("Nie udało się utworzyć grupy dla segmentu.");
      } else {
        setMessage(`Utworzono grupę (${code}) dla segmentu „${displaySegmentName(full, secretId)}”.`);
      }
    } catch (e) {
      rerr("[group] Błąd połączenia:", e);
      setMessage("Błąd sieci przy tworzeniu grupy.");
    } finally {
      setGroupBusy((m) => {
        const { [full]: _, ...rest } = m;
        return rest;
      });
    }
  };

  // [GROUP PICKER] — otwarcie i pobranie listy grup z masterami
  const openGroupPicker = async ({ shortName, fullName }) => {
    const sec = getSecretId();
    if (!sec) return;

    let full = fullName;
    if (!full || !hasSecretPivot(full, secretId)) {
      full = await resolveFullForEdit(shortName);
    }
    if (!full || !hasSecretPivot(full, secretId)) {
      rwarn("[group-picker] Nie udało się rozpoznać pełnej nazwy segmentu:", shortName);
      setMessage("Nie udało się rozpoznać segmentu do przypisania.");
      return;
    }

    setGroupPickerFor({ full, short: shortName });
    setGroupsLoading(true);
    setGroupsError("");
    setGroupPickerOpen(true);

    try {
      // UWAGA: endpoint ma w ścieżce {secredId}
      const url = `${API}/segments-with-master/${encodeURIComponent(sec)}`;
      rlog("→ segments-with-master:", url);
      const t0 = performance.now();
      const res = await fetch(url, { mode: "cors" });
      const txt = await res.text();
      let json = {};
      try { json = JSON.parse(txt); } catch { }
      rlog("← segments-with-master:", { status: res.status, ok: res.ok, time_s: ((performance.now() - t0) / 1000).toFixed(3), json });

      if (!res.ok || json?.success === false || !Array.isArray(json?.segments)) {
        setGroupsMasterList([]);
        setGroupsError("Nie udało się pobrać listy grup.");
        return;
      }

      const items = json.segments
        .map((s) => ({ segment_name: s.segment_name, group_id: s.group_id }))
        .filter((s) => s.segment_name && s.group_id);

      setGroupsMasterList(items);
    } catch (e) {
      rerr("[group-picker] Błąd pobierania listy grup:", e);
      setGroupsMasterList([]);
      setGroupsError("Błąd sieci przy pobieraniu listy grup.");
    } finally {
      setGroupsLoading(false);
    }
  };

  // [GROUP PICKER] — przypisz bieżący segment do wybranej grupy
  const assignToGroup = async (selectedGroupId) => {
    if (!groupPickerFor?.full || !selectedGroupId) return;
    const payload = {
      segment_name: groupPickerFor.full,
      group_id: String(selectedGroupId),
      group_master: 0,
    };

    try {
      const t0 = performance.now();
      const res = await fetch(`${API}/update-group-settings`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const txt = await res.text();
      let json = {};
      try { json = JSON.parse(txt); } catch { }
      rlog("← assign update-group-settings:", { status: res.status, ok: res.ok, time_s: ((performance.now() - t0) / 1000).toFixed(3), json });

      if (!res.ok || json?.success === false) {
        setMessage("Nie udało się przypisać segmentu do grupy.");
        return;
      }

      setMessage(`Segment „${displaySegmentName(groupPickerFor.full, secretId)}” przypisany do grupy ${selectedGroupId}.`);
      setGroupInfo((g) => ({
        ...g,
        [groupPickerFor.full]: { code: selectedGroupId, ok: true },
      }));
      setGroupPickerOpen(false);
      setGroupPickerFor(null);
    } catch (e) {
      rerr("[group-picker] Błąd przypisywania do grupy:", e);
      setMessage("Błąd sieci przy przypisywaniu do grupy.");
    }
  };

  if (!secretId) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light font-sans">
        <div className="text-center p-5">
          <div className="mb-4 text-primary" style={{ fontSize: '3rem' }}>⚠️</div>
          <h2 className="fw-bold text-dark">Brak identyfikatora</h2>
          <p className="text-secondary">Nie znaleziono ID wydarzenia. Spróbuj ponownie.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column align-items-center bg-light font-sans py-5 page-root-scrollable">

      <div className="w-100 px-3" style={{ maxWidth: '900px' }}>

        {/* STEPPER */}
        <div className="mb-5 px-md-5">
          <div className="d-flex align-items-center justify-content-between position-relative">
            {/* Line background */}
            <div className="position-absolute top-50 start-0 translate-middle-y w-100 bg-secondary opacity-25" style={{ height: '2px', zIndex: 0 }}></div>

            {[1, 2, 3].map((num) => {
              const isActive = step === num;
              const isCompleted = step > num;
              return (
                <div key={num} className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 1 }}>
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all ${isActive ? 'bg-primary text-white scale-110 shadow-primary' : isCompleted ? 'bg-success text-white' : 'bg-white text-secondary border'}`}
                    style={{ width: 48, height: 48, fontSize: '1.1rem', transition: 'all 0.3s ease' }}
                  >
                    {isCompleted ? '✓' : num}
                  </div>
                  <div className={`small fw-bold mt-2 text-uppercase ls-1 ${isActive ? 'text-primary' : 'text-secondary opacity-75'}`} style={{ fontSize: '0.7rem' }}>
                    {num === 1 && "Dane"}
                    {num === 2 && "Segmenty"}
                    {num === 3 && "Agenda"}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* CARD CONTAINER */}
        <div className="bg-white rounded-5 shadow-sm p-4 p-md-5 position-relative overflow-hidden animate-fade-in-up">

          {step === 1 && (
            <div className="animate-fade-in">
              <h2 className="fw-bold mb-4 text-dark h3">Uzupełnij dane wydarzenia</h2>
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-label text-uppercase small fw-bold text-secondary ls-1">Nazwa wydarzenia</label>
                  <input
                    name="eventName"
                    value={eventData.eventName}
                    onChange={handleChange}
                    className="form-control minimal-input"
                    placeholder="np. Konferencja Marketingowa 2024"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-uppercase small fw-bold text-secondary ls-1">Data</label>
                  <input
                    name="date"
                    type="date"
                    value={eventData.date}
                    onChange={handleChange}
                    onBlur={saveDateImmediate}
                    className="form-control minimal-input"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-uppercase small fw-bold text-secondary ls-1">Miejsce</label>
                  <input
                    name="adress"
                    value={eventData.adress}
                    onChange={handleChange}
                    className="form-control minimal-input"
                    placeholder="np. Warszawa, Złota 44"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label text-uppercase small fw-bold text-secondary ls-1">Organizator</label>
                  <input
                    name="organizator"
                    value={eventData.organizator}
                    onChange={handleChange}
                    className="form-control minimal-input"
                    placeholder="np. Twoja Firma"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in">
              <h2 className="fw-bold mb-4 text-dark h3">Logo i Segmenty</h2>

              {/* LOGO */}
              <div className="mb-5">
                <label className="form-label text-uppercase small fw-bold text-secondary ls-1 mb-3">Logo Wydarzenia</label>
                {eventData.eventImgUrl ? (
                  <div className="d-flex align-items-center gap-4 p-3 border rounded-4 bg-light">
                    <img src={eventData.eventImgUrl} alt="Logo" className="rounded-3 shadow-sm object-fit-contain bg-white" style={{ width: 80, height: 80 }} onError={() => setEventData(p => ({ ...p, eventImgUrl: "" }))} />
                    <div>
                      <button onClick={handleRemoveLogo} className="btn btn-outline-danger btn-sm rounded-pill px-3 fw-bold">Usuń logo</button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed rounded-4 text-center bg-light hover-bg-gray transition-all cursor-pointer position-relative">
                    <input type="file" accept="image/*" onChange={handleLogoFileChange} className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer" />
                    <div className="text-primary mb-2 display-6">+</div>
                    <div className="fw-bold text-dark">Kliknij, aby dodać logo</div>
                    <div className="small text-secondary">PNG, JPG (max 2MB)</div>
                  </div>
                )}
              </div>

              {/* SEGMENTS */}
              <div>
                <label className="form-label text-uppercase small fw-bold text-secondary ls-1 mb-3">Segmenty (Sale / Tematy)</label>
                <div className="d-flex gap-2 mb-4">
                  <input
                    value={segmentInput}
                    onChange={(e) => setSegmentInput(e.target.value)}
                    className="form-control minimal-input"
                    placeholder="np. Scena Główna, Warsztaty..."
                  />
                  <button onClick={addSegment} className="btn btn-primary rounded-3 px-4 fw-bold shadow-sm">Dodaj</button>
                </div>

                {segments.length > 0 ? (
                  <div className="d-flex flex-column gap-2" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: 4 }}>
                    <SwipeableList fullSwipe={false} type={ListType.IOS}>
                      {segments.map((seg, i) => {
                        const fullName = seg.full;
                        const display = seg.short || fullName;

                        const trailingActions = () => (
                          <TrailingActions>
                            <SwipeAction
                              onClick={() => {
                                rlog("Swipe Edit Clicked", seg);
                                const sName = seg.short || "";
                                const fName = seg.full || "";

                                // Fix: strictly match structure expected by PrelegentEditorModal { fullName, label }
                                setEditorRow({
                                  fullName: fName || "",
                                  label: sName,
                                  segment_name: fName || ""
                                });
                                setEditorOpen(true);

                                if (!fName && sName) {
                                  resolveFullForEdit(sName).then(resFull => {
                                    if (resFull) {
                                      setEditorRow(prev => ({
                                        ...prev,
                                        fullName: resFull,
                                        segment_name: resFull
                                      }));
                                    }
                                  });
                                }
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-center bg-secondary text-white shadow-sm" style={{ width: 70, borderRadius: 12, margin: '0 4px', height: '100%' }}>
                                <FontAwesomeIcon icon={faUserEdit} className="fs-5" />
                              </div>
                            </SwipeAction>
                            <SwipeAction
                              destructive={false}
                              onClick={() => {
                                const target = fullName || seg.short;
                                rlog("Swipe Delete Clicked target:", target);
                                if (!target) {
                                  setMessage("Błąd: Nie udało się zidentyfikować segmentu.");
                                  return;
                                }
                                requestDelete(target);
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-center bg-danger text-white shadow-sm" style={{ width: 70, borderRadius: 12, margin: '0 0 0 4px', height: '100%' }}>
                                <FontAwesomeIcon icon={faTrash} className="fs-5" />
                              </div>
                            </SwipeAction>
                          </TrailingActions>
                        );

                        return (
                          <SwipeableListItem
                            key={i}
                            trailingActions={trailingActions()}
                            className="mb-2"
                          >
                            <div className="position-relative w-100">
                              {/* FAKE ACTIONS FOR TUTORIAL */}
                              {i === tutorialTarget && (
                                <div className="d-flex align-items-center position-absolute top-0 end-0 h-100" style={{ zIndex: 0, paddingRight: '4px' }}>
                                  <div className="d-flex align-items-center justify-content-center bg-secondary text-white shadow-sm" style={{ width: 70, borderRadius: 12, margin: '0 4px', height: '100%' }}>
                                    <FontAwesomeIcon icon={faUserEdit} className="fs-5" />
                                  </div>
                                  <div className="d-flex align-items-center justify-content-center bg-danger text-white shadow-sm" style={{ width: 70, borderRadius: 12, margin: '0 0 0 4px', height: '100%' }}>
                                    <FontAwesomeIcon icon={faTrash} className="fs-5" />
                                  </div>
                                </div>
                              )}

                              <div className={`d-flex align-items-center justify-content-between p-3 border rounded-3 bg-white shadow-hover transition-all w-100 position-relative re-swipe-item ${i === tutorialTarget ? 'tutorial-swipe' : ''}`} style={{ zIndex: 1 }}>
                                <div className="fw-bold text-dark">{display}</div>
                                <div className="text-secondary small fst-italic re-swipe-hint">
                                  Przesuń <span style={{ fontSize: '1.2em' }}>←</span>
                                </div>
                              </div>
                            </div>
                          </SwipeableListItem>
                        )
                      })}
                    </SwipeableList>
                  </div>
                ) : (
                  <div className="text-center text-muted small py-4 bg-light rounded-3 border-dashed">
                    Brak zdefiniowanych segmentów.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in">
              <h2 className="fw-bold mb-4 text-dark h3">Agenda</h2>
              <div className="bg-light p-3 rounded-4 border">
                <AgendaBar
                  agenda={agenda}
                  setAgenda={setAgenda}
                  agendaInput={agendaInput}
                  setAgendaInput={setAgendaInput}
                  showAgendaInput={showAgendaInput}
                  setShowAgendaInput={setShowAgendaInput}
                  autoOpen={true}
                />
              </div>
            </div>
          )}

          {/* FOOTER ACTIONS */}
          <div className="d-flex align-items-center justify-content-between mt-5 pt-4 border-top">
            <button
              disabled={step === 1}
              onClick={() => setStep(s => Math.max(1, s - 1))}
              className="btn btn-link text-secondary text-decoration-none fw-bold px-0"
            >
              ← Wróć
            </button>

            {step < totalSteps ? (
              <button
                disabled={step === 1 && !isStep1Valid}
                onClick={() => setStep(s => Math.min(totalSteps, s + 1))}
                className="btn btn-primary rounded-pill px-5 py-3 fw-bold shadow-primary hover-lift"
              >
                Dalej
              </button>
            ) : (
              <button
                onClick={handleSaveAll}
                disabled={isSaving}
                className="btn btn-success rounded-pill px-5 py-3 fw-bold shadow-sm hover-lift text-white"
              >
                {isSaving ? "Zapisywanie..." : "Zapisz i Zakończ"}
              </button>
            )}
          </div>

          {message && <div className="mt-3 alert alert-info small text-center rounded-3">{message}</div>}

        </div>
      </div>

      {/* MODALS */}
      {groupPickerOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-50 d-flex align-items-center justify-content-center" style={{ zIndex: 2000 }}>
          <div className="bg-white rounded-4 shadow p-4 w-100 mx-3" style={{ maxWidth: '400px' }}>
            <h4 className="fw-bold mb-3">Przypisz grupę</h4>
            {groupsLoading && <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"></div></div>}
            {!groupsLoading && groupsMasterList.map(g => (
              <button key={g.group_id} onClick={() => assignToGroup(g.group_id)} className="btn btn-light w-100 text-start mb-2 rounded-3 border">
                <div className="fw-bold">{displaySegmentName(g.segment_name, secretId)}</div>
                <div className="small text-secondary">ID: {g.group_id}</div>
              </button>
            ))}
            <button onClick={() => setGroupPickerOpen(false)} className="btn btn-link text-secondary w-100 mt-2 text-decoration-none">Anuluj</button>
          </div>
        </div>
      )}

      {/* MODALS MOVED TO ROOT */}
      <div style={{ position: 'relative', zIndex: 10000 }}>
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      </div>
      {editorRow && (
        <PrelegentEditorModal
          key={editorRow.segment_name || 'editor'}
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          secretId={secretId}
          segmentRow={editorRow}
          onSaved={async () => { await refreshStep2Data(secretId); }}
          debug={true}
        />
      )}

      <style>{`
          .page-root-scrollable { height: 100dvh; overflow-y: auto; }
          .font-sans { font-family: 'Check', 'Inter', system-ui, -apple-system, sans-serif; }
          .ls-1 { letter-spacing: 1px; }
          .minimal-input { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 12px; padding: 14px; font-size: 1rem; transition: all 0.2s; }
          .minimal-input:focus { background: #fff; border-color: var(--bs-primary); box-shadow: 0 0 0 4px rgba(var(--bs-primary-rgb), 0.1); }
          .shadow-primary { box-shadow: 0 10px 20px -5px rgba(var(--bs-primary-rgb), 0.3); }
          .shadow-hover:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
          .scale-110 { transform: scale(1.1); }
          .hover-lift:hover { transform: translateY(-2px); }
          .animate-fade-in-up { animation: fadeInUp 0.4s ease-out; }
          .animate-fade-in { animation: fadeIn 0.3s ease-out; }
          .animate-fade-in { animation: fadeIn 0.3s ease-out; }
          .re-swipe-hint { opacity: 0; transition: opacity 0.3s; }
          .re-swipe-item:hover .re-swipe-hint { opacity: 0.5; }
          .tutorial-swipe { animation: tutorialSwipeAnim 1.5s ease-in-out; }
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes tutorialSwipeAnim {
            0% { transform: translateX(0); }
            20% { transform: translateX(-100px); }
            50% { transform: translateX(-100px); }
            100% { transform: translateX(0); }
          }
      `}</style>
    </div>
  );
}
